import { useState, useEffect, useCallback } from 'react';
import {
  Globe, Navigation, Key, Plus, Trash2, ChevronUp, ChevronDown,
  Eye, EyeOff, Copy, Check, RefreshCw, AlertCircle, ExternalLink,
} from 'lucide-react';
import { useProject } from '../../../contexts/ProjectContext';
import { contentApi, type ContentItem } from '../../../api/content';

// ── Types ──────────────────────────────────────────────────────────────────────

interface SiteSettings {
  site_name: string;
  tagline: string;
  logo_url: string;
  cta_label: string;
  cta_url: string;
  twitter_url: string;
  linkedin_url: string;
  github_url: string;
}

interface NavEntry {
  label: string;
  url: string;
}

interface CmsApiKey {
  id: string;
  label: string;
  workspace_id: string;
  scopes: string[];
  is_active: boolean;
  created_at: string;
  last_used_at: string | null;
}

const DEFAULT_SETTINGS: SiteSettings = {
  site_name: '',
  tagline: '',
  logo_url: '',
  cta_label: '',
  cta_url: '',
  twitter_url: '',
  linkedin_url: '',
  github_url: '',
};

// ── Tab: Site Settings ─────────────────────────────────────────────────────────

function SiteSettingsTab({ workspaceId }: { workspaceId: string }) {
  const [itemId, setItemId] = useState<string | null>(null);
  const [settings, setSettings] = useState<SiteSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const items = await contentApi.listItems(workspaceId, { content_type: 'site_settings' });
        const item = items[0];
        if (item) {
          setItemId(item.id);
          try {
            const parsed = item.notes ? JSON.parse(item.notes) as SiteSettings : {};
            setSettings({ ...DEFAULT_SETTINGS, ...parsed, site_name: parsed.site_name ?? item.title });
          } catch {
            setSettings({ ...DEFAULT_SETTINGS, site_name: item.title });
          }
        }
      } catch (err) {
        console.error('Failed to load site settings', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [workspaceId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        title: settings.site_name || 'Site Settings',
        notes: JSON.stringify(settings),
      };
      if (itemId) {
        await contentApi.updateItem(workspaceId, itemId, payload as Partial<ContentItem>);
      } else {
        const created = await contentApi.createItem(workspaceId, {
          ...payload,
          content_type: 'site_settings',
          status: 'published',
        } as Partial<ContentItem>);
        setItemId(created.id);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('Failed to save site settings', err);
    } finally {
      setSaving(false);
    }
  };

  const field = (key: keyof SiteSettings, label: string, placeholder = '', type = 'text') => (
    <div>
      <label className="text-xs font-medium text-neutral-600 mb-1.5 block">{label}</label>
      <input
        type={type}
        value={settings[key]}
        onChange={e => setSettings(prev => ({ ...prev, [key]: e.target.value }))}
        placeholder={placeholder}
        className="w-full text-sm px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-400"
      />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw size={18} className="text-neutral-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-xl space-y-6">
      <div className="space-y-4">
        <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Identity</h3>
        {field('site_name', 'Site Name', 'Martha — AI for serious work')}
        {field('tagline', 'Tagline', 'The intelligence layer for your team')}

        <div>
          <label className="text-xs font-medium text-neutral-600 mb-1.5 block">Logo URL</label>
          <input
            value={settings.logo_url}
            onChange={e => setSettings(prev => ({ ...prev, logo_url: e.target.value }))}
            placeholder="https://..."
            className="w-full text-sm px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-400"
          />
          {settings.logo_url && (
            <div className="mt-2 p-3 border border-neutral-100 rounded-lg bg-neutral-50 flex items-center gap-3">
              <img src={settings.logo_url} alt="Logo preview" className="h-8 object-contain" onError={e => (e.currentTarget.style.display = 'none')} />
              <span className="text-xs text-neutral-500">Preview</span>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Primary CTA</h3>
        <div className="grid grid-cols-2 gap-4">
          {field('cta_label', 'Button Label', 'Get Started')}
          {field('cta_url', 'Button URL', 'https://app.heyarchie.ai')}
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Social Links</h3>
        {field('twitter_url', 'Twitter / X', 'https://x.com/heyarchie')}
        {field('linkedin_url', 'LinkedIn', 'https://linkedin.com/company/archie')}
        {field('github_url', 'GitHub', 'https://github.com/heyarchie')}
      </div>

      <div className="pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all
            ${saved
              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
              : 'bg-coral-500 hover:bg-coral-600 text-white disabled:opacity-50'
            }`}
        >
          {saved ? <><Check size={14} /> Saved</> : saving ? <><RefreshCw size={14} className="animate-spin" /> Saving...</> : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

// ── Tab: Navigation ────────────────────────────────────────────────────────────

function NavEditor({
  label,
  slug,
  workspaceId,
}: {
  label: string;
  slug: string;
  workspaceId: string;
}) {
  const [itemId, setItemId] = useState<string | null>(null);
  const [entries, setEntries] = useState<NavEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const items = await contentApi.listItems(workspaceId, { content_type: 'navigation' });
        const item = items.find(i => i.slug === slug);
        if (item) {
          setItemId(item.id);
          try {
            const parsed = item.notes ? JSON.parse(item.notes) as NavEntry[] : [];
            setEntries(Array.isArray(parsed) ? parsed : []);
          } catch { setEntries([]); }
        }
      } catch { setEntries([]); }
      finally { setLoading(false); }
    })();
  }, [workspaceId, slug]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        title: `${label} Navigation`,
        slug,
        content_type: 'navigation' as const,
        status: 'published' as const,
        notes: JSON.stringify(entries),
      };
      if (itemId) {
        await contentApi.updateItem(workspaceId, itemId, payload as Partial<ContentItem>);
      } else {
        const created = await contentApi.createItem(workspaceId, payload as Partial<ContentItem>);
        setItemId(created.id);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) { console.error('Failed to save navigation', err); }
    finally { setSaving(false); }
  };

  const move = (idx: number, dir: -1 | 1) => {
    const next = [...entries];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setEntries(next);
  };

  if (loading) return <div className="py-4 flex justify-center"><RefreshCw size={14} className="text-neutral-400 animate-spin" /></div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-neutral-700">{label}</h4>
        <button
          onClick={() => setEntries(prev => [...prev, { label: '', url: '' }])}
          className="flex items-center gap-1.5 text-xs text-coral-600 hover:text-coral-700 font-medium px-2 py-1 rounded-lg hover:bg-coral-50 transition-colors"
        >
          <Plus size={13} /> Add item
        </button>
      </div>

      {entries.length === 0 ? (
        <p className="text-xs text-neutral-400 py-3 text-center border border-dashed border-neutral-200 rounded-lg">No items. Click "Add item" to start.</p>
      ) : (
        <div className="space-y-2">
          {entries.map((entry, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="flex flex-col gap-0.5">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-0.5 text-neutral-400 hover:text-neutral-600 disabled:opacity-30 transition-colors"><ChevronUp size={12} /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === entries.length - 1} className="p-0.5 text-neutral-400 hover:text-neutral-600 disabled:opacity-30 transition-colors"><ChevronDown size={12} /></button>
              </div>
              <input
                value={entry.label}
                onChange={e => setEntries(prev => prev.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))}
                placeholder="Label"
                className="flex-1 text-sm px-2.5 py-1.5 border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-400"
              />
              <input
                value={entry.url}
                onChange={e => setEntries(prev => prev.map((x, i) => i === idx ? { ...x, url: e.target.value } : x))}
                placeholder="/path or https://..."
                className="flex-[2] text-sm px-2.5 py-1.5 border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-400"
              />
              <button
                onClick={() => setEntries(prev => prev.filter((_, i) => i !== idx))}
                className="p-1.5 text-neutral-400 hover:text-red-500 transition-colors"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-medium transition-all
          ${saved ? 'bg-emerald-100 text-emerald-700' : 'bg-coral-500 hover:bg-coral-600 text-white disabled:opacity-50'}`}
      >
        {saved ? <><Check size={13} /> Saved</> : saving ? 'Saving...' : `Save ${label}`}
      </button>
    </div>
  );
}

function NavigationTab({ workspaceId }: { workspaceId: string }) {
  return (
    <div className="max-w-xl space-y-8">
      <NavEditor label="Header" slug="header" workspaceId={workspaceId} />
      <div className="border-t border-neutral-100" />
      <NavEditor label="Footer" slug="footer" workspaceId={workspaceId} />
    </div>
  );
}

// ── Tab: API Keys ──────────────────────────────────────────────────────────────

function ApiKeysTab({ workspaceId }: { workspaceId: string }) {
  const [keys, setKeys] = useState<CmsApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/cms/keys', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json() as { keys: CmsApiKey[] };
        setKeys(data.keys);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void loadKeys(); }, [loadKeys]);

  const handleGenerate = async () => {
    if (!newLabel.trim()) return;
    setGenerating(true);
    try {
      const res = await fetch('/api/cms/keys', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: newLabel.trim(), workspace_id: workspaceId }),
      });
      if (res.ok) {
        const data = await res.json() as { key: string };
        setGeneratedKey(data.key);
        setNewLabel('');
        void loadKeys();
      }
    } catch { /* ignore */ }
    finally { setGenerating(false); }
  };

  const handleRevoke = async (id: string) => {
    if (!window.confirm('Revoke this API key? Any services using it will stop working.')) return;
    try {
      await fetch(`/api/cms/keys/${id}`, { method: 'DELETE', credentials: 'include' });
      void loadKeys();
    } catch { /* ignore */ }
  };

  const handleCopyKey = async () => {
    if (!generatedKey) return;
    await navigator.clipboard.writeText(generatedKey);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
  };

  return (
    <div className="max-w-2xl space-y-6">
      {/* Generate new key */}
      <div className="p-4 border border-neutral-200 rounded-xl space-y-3">
        <h3 className="text-sm font-semibold text-neutral-700">Generate New Key</h3>
        <p className="text-xs text-neutral-500">API keys allow your website (heyarchie.ai) to read published content via the headless delivery API. Send as <code className="text-xs bg-neutral-100 px-1 py-0.5 rounded">X-CMS-Key</code> header.</p>
        <div className="flex gap-2">
          <input
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && void handleGenerate()}
            placeholder="e.g. heyarchie.ai production"
            className="flex-1 text-sm px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-400"
          />
          <button
            onClick={handleGenerate}
            disabled={!newLabel.trim() || generating}
            className="px-4 py-2 rounded-lg bg-coral-500 hover:bg-coral-600 text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {generating ? <RefreshCw size={13} className="animate-spin" /> : <Key size={13} />}
            Generate
          </button>
        </div>

        {/* Show generated key once */}
        {generatedKey && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
            <div className="flex items-center gap-2 text-amber-700">
              <AlertCircle size={14} />
              <span className="text-xs font-semibold">Copy this key now — it won't be shown again</span>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-white border border-amber-200 px-3 py-2 rounded-lg font-mono text-neutral-800 truncate">
                {generatedKey}
              </code>
              <button
                onClick={handleCopyKey}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all shrink-0
                  ${keyCopied ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}
              >
                {keyCopied ? <Check size={13} /> : <Copy size={13} />}
                {keyCopied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Key list */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-700 mb-3">Active Keys</h3>
        {loading ? (
          <div className="flex justify-center py-8"><RefreshCw size={16} className="text-neutral-400 animate-spin" /></div>
        ) : keys.length === 0 ? (
          <p className="text-sm text-neutral-400 text-center py-8 border border-dashed border-neutral-200 rounded-xl">No API keys yet.</p>
        ) : (
          <div className="space-y-2">
            {keys.map(k => (
              <div key={k.id} className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-colors
                ${k.is_active ? 'border-neutral-200 bg-white' : 'border-neutral-100 bg-neutral-50 opacity-60'}`}>
                <div className="space-y-0.5 min-w-0">
                  <div className="flex items-center gap-2">
                    <Key size={13} className="text-neutral-400 shrink-0" />
                    <span className="text-sm font-medium text-neutral-700 truncate">{k.label}</span>
                    {!k.is_active && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-neutral-200 text-neutral-500 rounded-full font-medium shrink-0">Revoked</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 ml-5 text-[10px] text-neutral-400">
                    <span>Created {new Date(k.created_at).toLocaleDateString()}</span>
                    {k.last_used_at && <span>Last used {new Date(k.last_used_at).toLocaleDateString()}</span>}
                    <span className="font-mono">{k.scopes.join(', ')}</span>
                  </div>
                </div>
                {k.is_active && (
                  <button
                    onClick={() => void handleRevoke(k.id)}
                    className="text-xs text-red-500 hover:text-red-600 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors shrink-0"
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick test */}
      <div className="p-4 bg-neutral-50 border border-neutral-200 rounded-xl space-y-2">
        <h3 className="text-xs font-semibold text-neutral-600">Quick Test</h3>
        <p className="text-xs text-neutral-500">Test the delivery API endpoint:</p>
        <code className="block text-xs font-mono text-neutral-700 bg-white border border-neutral-200 px-3 py-2 rounded-lg">
          curl /api/cms/articles -H "X-CMS-Key: YOUR_KEY"
        </code>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

type TabId = 'settings' | 'navigation' | 'keys';

const TABS: Array<{ id: TabId; label: string; icon: React.ReactNode }> = [
  { id: 'settings', label: 'Site Settings', icon: <Globe size={14} /> },
  { id: 'navigation', label: 'Navigation', icon: <Navigation size={14} /> },
  { id: 'keys', label: 'API Keys', icon: <Key size={14} /> },
];

const CmsSettingsPage = () => {
  const { currentProject } = useProject();
  const workspaceId = currentProject?.workspaceId;
  const [activeTab, setActiveTab] = useState<TabId>('settings');

  if (!workspaceId) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-neutral-500">No workspace selected</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="px-6 py-4 border-b border-neutral-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-base font-semibold text-neutral-800">CMS Settings</h1>
            <p className="text-xs text-neutral-400 mt-0.5">Configure your website, navigation, and delivery API</p>
          </div>
          <a
            href="https://heyarchie.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-700 transition-colors"
          >
            <ExternalLink size={12} /> heyarchie.ai
          </a>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                ${activeTab === tab.id
                  ? 'bg-coral-50 text-coral-700'
                  : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
                }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        {activeTab === 'settings' && <SiteSettingsTab workspaceId={workspaceId} />}
        {activeTab === 'navigation' && <NavigationTab workspaceId={workspaceId} />}
        {activeTab === 'keys' && <ApiKeysTab workspaceId={workspaceId} />}
      </div>
    </div>
  );
};

export default CmsSettingsPage;
